import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';

/**
 * Reusable controller component for clearing card selection
 * Used by both PropertyShowChart and PortfolioShowChart components
 * @param {object} props
 * @param {boolean} props.enableClearBtn - Whether the clear button should be enabled
 * @param {boolean} props.hasAnySelectedKeys - Whether any view has selected keys
 * @param {boolean} props.hasCurrentViewSelectedKeys - Whether current view has selected keys
 * @param {Function} props.onClearAllSelectionsFunc - Handler for clearing all selections
 * @param {Function} props.onClearCurrentSelectionFunc - Handler for clearing current view selection
 * @returns {React.ReactElement|null} The component or null if not shown
 */
function CardSelectionController({
  enableClearBtn,
  hasAnySelectedKeys,
  hasCurrentViewSelectedKeys,
  onClearAllSelectionsFunc,
  onClearCurrentSelectionFunc,
}) {
  if (!onClearCurrentSelectionFunc || !onClearAllSelectionsFunc) {
    return null;
  }

  const intl = useIntl();

  // Use generic i18n keys that work for both property and portfolio contexts
  const clearLabel = intl.formatMessage({
    id: 'dashboard.card_selection_controller.clear_toggle.label',
    defaultMessage: 'Clear',
  });

  const currentSelectionLabel = intl.formatMessage({
    id: 'dashboard.card_selection_controller.dropdown_item.current_selection.label',
    defaultMessage: 'Current selection',
  });

  const allLabel = intl.formatMessage({
    id: 'dashboard.card_selection_controller.dropdown_item.all.label',
    defaultMessage: 'All',
  });

  return (
    <UncontrolledDropdown>
      <DropdownToggle
        caret
        disabled={!enableClearBtn}
        className="btn-darken"
      >
        {clearLabel}
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem
          disabled={!hasCurrentViewSelectedKeys}
          onClick={onClearCurrentSelectionFunc}
        >
          {currentSelectionLabel}
        </DropdownItem>
        <DropdownItem
          disabled={!hasAnySelectedKeys}
          onClick={onClearAllSelectionsFunc}
        >
          {allLabel}
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

CardSelectionController.propTypes = {
  enableClearBtn: PropTypes.bool.isRequired,
  hasAnySelectedKeys: PropTypes.bool.isRequired,
  hasCurrentViewSelectedKeys: PropTypes.bool.isRequired,
  onClearAllSelectionsFunc: PropTypes.func.isRequired,
  onClearCurrentSelectionFunc: PropTypes.func.isRequired,
};

export default CardSelectionController;
